import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelloSign from 'hellosign-embedded';
import PropTypes from 'prop-types';
import { AppModal } from '../AppModal';
import { previewSignIcf, getSignIcfPiUrl, completeSignIcf } from '../../services/lighthouse.service';

const COMPLETE_ATTEMPTS = 5;
const COMPLETE_RETRY_MS = 800;

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Sign ICF modal — matches onyxbio/lighthouse mock-image-modal chrome:
 * X close, Official name / Email meta grid, Dropbox Sign document surface in-body.
 *
 * The embed host must stay mounted and sized before HelloSign.open() — the SDK
 * injects an iframe and immediately postMessages it; a display:none / zero-height
 * host yields contentWindow === null.
 */
export function SignIcfReviewModal({
  open,
  onClose,
  token,
  userId,
  onSigningStart,
  onSigningFailed,
  onSigned,
}) {
  const [loading, setLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [embedReady, setEmbedReady] = useState(false);
  const clientRef = useRef(null);
  const containerRef = useRef(null);
  const completedRef = useRef(false);
  // Keep latest callbacks out of the HelloSign effect deps so parent re-renders
  // from onSigningStart (row pending spinner) cannot cancel finalize mid-retry.
  const onCloseRef = useRef(onClose);
  const onSigningStartRef = useRef(onSigningStart);
  const onSigningFailedRef = useRef(onSigningFailed);
  const onSignedRef = useRef(onSigned);

  useEffect(() => {
    onCloseRef.current = onClose;
    onSigningStartRef.current = onSigningStart;
    onSigningFailedRef.current = onSigningFailed;
    onSignedRef.current = onSigned;
  }, [onClose, onSigningStart, onSigningFailed, onSigned]);

  const closeClient = () => {
    if (!clientRef.current) return;
    try {
      clientRef.current.close();
    } catch {
      // already closed
    }
    clientRef.current = null;
  };

  useEffect(() => {
    if (!open) {
      completedRef.current = false;
      setPreview(null);
      setError(null);
      setEmbedReady(false);
      setFinalizing(false);
      closeClient();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !userId || !token) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEmbedReady(false);
    setFinalizing(false);

    (async () => {
      const { data, error: err } = await previewSignIcf(token, userId);
      if (cancelled) return;
      if (err) {
        setLoading(false);
        setError(typeof err === 'string' ? err : err?.message || 'Failed to load ICF');
        return;
      }
      setPreview(data);
      setLoading(false);

      if (!data?.eligible || !data?.document_id) {
        return;
      }

      // Show a sized host, then wait for paint before HelloSign.open().
      setEmbedReady(true);
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (cancelled || !containerRef.current) return;

      const { data: signData, error: signErr } = await getSignIcfPiUrl(
        token,
        userId,
        data.document_id
      );
      if (cancelled) return;
      if (signErr || !signData?.sign_url) {
        setEmbedReady(false);
        setError(signErr?.message || signData?.error || 'Failed to open signing URL');
        return;
      }

      closeClient();
      const clientId = signData.client_id || process.env.REACT_APP_DROPBOX_SIGN_CLIENT_ID;
      const client = new HelloSign({ clientId });
      clientRef.current = client;

      client.on('sign', async () => {
        completedRef.current = true;
        setFinalizing(true);
        setEmbedReady(false);
        closeClient();
        if (onSigningStartRef.current) onSigningStartRef.current(userId);
        try {
          let lastError = null;
          let ok = false;
          for (let attempt = 0; attempt < COMPLETE_ATTEMPTS; attempt += 1) {
            // Ignore effect-cleanup `cancelled` once finalize has started — parent
            // pending-state updates must not abort Dropbox confirmation retries.
            const { data: completeData, error: completeErr } = await completeSignIcf(
              token,
              userId
            );
            if (completeErr) {
              lastError = completeErr.message || String(completeErr);
              break;
            }
            if (completeData?.ok) {
              ok = true;
              break;
            }
            if (completeData?.reason === 'not_fully_consented') {
              lastError = 'Waiting for Dropbox Sign to confirm countersign…';
              await sleep(COMPLETE_RETRY_MS * (attempt + 1));
              continue;
            }
            lastError = completeData?.reason || 'Failed to finalize countersign';
            break;
          }
          if (!ok) {
            setError(lastError || 'Failed to finalize countersign');
            setFinalizing(false);
            if (onSigningFailedRef.current) onSigningFailedRef.current(userId);
            return;
          }
          if (onSignedRef.current) onSignedRef.current(userId);
          if (onCloseRef.current) onCloseRef.current();
        } catch (e) {
          setError(e?.message || 'Failed to finalize countersign');
          setFinalizing(false);
          if (onSigningFailedRef.current) onSigningFailedRef.current(userId);
        }
      });

      client.on('close', () => {
        clientRef.current = null;
      });

      client.on('error', (hsErr) => {
        console.error('HelloSign error', hsErr);
        if (cancelled || completedRef.current) return;
        setError('Signing was interrupted. Close and try again.');
      });

      try {
        client.open(signData.sign_url, {
          clientId,
          skipDomainVerification: true,
          container: containerRef.current,
        });
      } catch (e) {
        if (!cancelled) {
          setEmbedReady(false);
          setError(e?.message || 'Failed to open Dropbox Sign');
        }
      }
    })();

    return () => {
      // Do not abort an in-flight finalize when parent re-renders (e.g. row pending).
      if (completedRef.current) return;
      cancelled = true;
      closeClient();
    };
  }, [open, userId, token]);

  const officialName = preview?.name || '—';
  const dob = preview?.dob || '—';
  const email = preview?.email || '—';
  // Keep the host sized for the whole session — toggling display:none after
  // HelloSign.open() tears down contentWindow and triggers postMessage errors.
  const showEmbed = embedReady && !finalizing;

  return (
    <AppModal
      open={open}
      onClose={finalizing ? () => {} : onClose}
      title={null}
      showCloseButton={false}
      actions={null}
      dividers={false}
      maxWidth="lg"
      className="onyx-document-modal"
      aria-label="Sign ICF"
    >
      <div className="onyx-sign-icf" data-testid="onyx-sign-icf">
        <IconButton
          aria-label="Close"
          onClick={onClose}
          className="onyx-sign-icf__close"
          size="small"
          disabled={finalizing}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <div className="onyx-sign-icf__header">
          <span className="onyx-sign-icf__meta-label">Official name</span>
          <span className="onyx-sign-icf__meta-value">{officialName}</span>
          <span className="onyx-sign-icf__meta-label">DOB</span>
          <span className="onyx-sign-icf__meta-value">{dob}</span>
          <span className="onyx-sign-icf__meta-label">Email</span>
          <span className="onyx-sign-icf__meta-value">{email}</span>
        </div>

        <div className="onyx-sign-icf__document">
          {loading || finalizing ? (
            <div className="onyx-sign-icf__status" data-testid="onyx-sign-icf-finalizing">
              <CircularProgress size={28} />
              <Typography variant="uiBody">
                {finalizing ? 'Finalizing countersign…' : 'Loading ICF…'}
              </Typography>
            </div>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {!loading && !finalizing && preview && !preview.eligible ? (
            <Alert severity="info">No PI-signable consent for this member.</Alert>
          ) : null}
          <div
            ref={containerRef}
            className={`onyx-sign-icf__embed${showEmbed ? ' onyx-sign-icf__embed--active' : ''}`}
            data-testid="onyx-sign-icf-embed"
            aria-hidden={!showEmbed}
          />
        </div>
      </div>
    </AppModal>
  );
}

SignIcfReviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  token: PropTypes.string,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSigningStart: PropTypes.func,
  onSigningFailed: PropTypes.func,
  onSigned: PropTypes.func,
};
